import React, { useState, useEffect, useRef, useMemo } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Container,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	Alert,
	Link,
	Divider,
	Skeleton,
} from "@mui/material";
import {
	Add as AddIcon,
	Remove as RemoveIcon,
	DeleteOutline as DeleteIcon,
	WarningAmber,
	RemoveShoppingCartOutlined,
} from "@mui/icons-material";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";

import "../Cart/Cart.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { ASSETS_BASE_URL, DEFAULT_LAYOUT } from "../../config";
import { useAuth } from "../../context/authContext";
import {
	ALERT_MESSAGES,
	BUTTON,
	ROUTES,
	TABLE_HEADERS,
	TITLES,
	UI_TEXT,
} from "../../constants";
import { useTranslation } from "react-i18next";
import formatCurrency from "../../utilities/currency";
import CartTable from "../../components/CartTable/CartTable";
import { getProductsById, getOptions, getCartRecommendations, getCartCTP } from "../../services/cartService";
import CartUpload from "../../components/CartUpload/CartUpload";
import { toast } from "../../context/ToastContext";

// Helper function to get product ID, handling both 'ProductID' and 'product_id'
const getNormalizedProductId = (item) => item?.ProductID || item?.product_id;

const Cart = () => {

	const { cartItems, removeFromCart, clearCart, updateCart, isInitialized, refreshCart } = useCart();
	const { selectedSite, selectedSiteCurrency, currencySymbol, selectedSiteData } = useSite();
	const { user, hasPermission } = useAuth();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [cartProducts, setCartProducts] = useState([]);
	const [groupedCartProducts, setGroupedCartProducts] = useState([]);
	const [isLocalInitialized, setIsLocalInitialized] = useState(false);
	const [recommendedProducts, setRecommendedProducts] = useState([]);
	const [showDisclaimerCheckout, setShowDisclaimerCheckout] = useState(false)
	// Optimized quantity management state
	const [quantityInputs, setQuantityInputs] = useState({});
	const [modelViewerSummary, setModelViewerSummary] = useState(null);
	const [pendingMixedSummary, setPendingMixedSummary] = useState(null);

	const prodsRef = useRef(null);
    const ctpDebounceRef = useRef(null); // debounce timer for CTP refresh
    const ctpReqSeq = useRef(0); // track last CTP request to discard stale responses
    const recDebounceRef = useRef(null); // debounce timer for FP (frequently purchased) recommendations
    const recReqSeq = useRef(0); // track last recommendations request
	const { t } = useTranslation();
	const triggerToast = (message, severity = "info") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};
	const [appOptions, setAppOptions] = useState({
		currency: "USD",
		minOrderAmount: 0,
		maxOrderQty: 999,
		creditCardFee: 2.5,
		taxRate: 5,
		shippingCost: 10,
		leadTime: 3,
	});
	const maxQtyAllowed = useMemo(() => {
		const parsed = parseInt(appOptions?.maxOrderQty, 10);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : 999;
	}, [appOptions]);

	const canPlaceOrder = hasPermission("order_management.create_order")
	const canCreateOrderSchedule = hasPermission("order_management.create_order_schedule")
	const navigate = useNavigate();
	const location = useLocation();

	const handleNavigateToProduct = (url) => {
		navigate(url);
	};

	useEffect(() => {
		if (!pendingMixedSummary) return;
		setModelViewerSummary(pendingMixedSummary);
		setPendingMixedSummary(null);
		if (pendingMixedSummary?.requestCount && !pendingMixedSummary.cartCount) {
			navigate("/requests");
		}
	}, [pendingMixedSummary, navigate]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const mvSource = params.get("mvSource");
		if (mvSource) {
			const cartCount = parseInt(params.get("mvCartCount"), 10) || 0;
			const requestCount = parseInt(params.get("mvRequestCount"), 10) || 0;
			setModelViewerSummary({ cartCount, requestCount, source: mvSource });
			setLoading(true);
			setIsLocalInitialized(false);
			refreshCart();
		} else {
			setModelViewerSummary(null);
		}
	}, [location.search, refreshCart]);

	// Check if product image exists
	const checkImageExists = async (productNumber) => {
		const productImageUrl = `${ASSETS_BASE_URL}/ProductImages/TMB/${productNumber}.JPG`;
		try {
			const response = await fetch(productImageUrl, { method: "HEAD" });
			return response.ok ? productImageUrl : `${ASSETS_BASE_URL}/stock.png`;
		} catch (error) {
			console.warn("Product image not found, using default image.");
			return `${ASSETS_BASE_URL}/stock.png`;
		}
	};

	const productsInCart = useMemo(() => {
		if (selectedSite) {
			const filteredCartItems = cartItems?.filter((item) => item !== null && item !== undefined && item.Site === selectedSite) || [];
			return filteredCartItems;
		} else {
			return []
		}

	}, [cartItems, selectedSite]);

	const uniqueProductIds = useMemo(
		() =>
			[...new Set(productsInCart.map((p) => getNormalizedProductId(p)).filter(Boolean))],
		[productsInCart]
	);

	const cartProductIds = useMemo(
		() => uniqueProductIds.slice().sort().join(","),
		[uniqueProductIds]
	);


	const fetchCTPForItems = async (cartItems) => {
		try {
			const req = {
				items: cartItems.map((cp) => ({
					product_id: getNormalizedProductId(cp),
					quantity: cp?.Quantity || 1,
				})),
				site_number: selectedSite,
			};
			const resp = await getCartCTP(req);
			const data = (resp?.data || []).filter(d => d !== null && d !== undefined);
			const byId = new Map(data.map((d) => [String(d.product_id), d.ctp]));
			return cartItems.map((cp) => {
				if (!cp) return null; // Defensive null check
				const pid = String(getNormalizedProductId(cp));
				const ctp = byId.get(pid);
				return ctp ? { ...cp, ctp } : cp;
			}).filter(cp => cp !== null);
		} catch (e) {
			console.error('Failed to fetch CTP:', e);
			return cartItems;
		}
	};

	// Debounced CTP updater: optimistic UI first, then refresh CTP once idle
	const scheduleCTPRefresh = (items) => {
		if (ctpDebounceRef.current) clearTimeout(ctpDebounceRef.current);
		const seq = ++ctpReqSeq.current;
		ctpDebounceRef.current = setTimeout(async () => {
			try {
				const withCTP = await fetchCTPForItems(items);
				if (seq === ctpReqSeq.current) {
					setCartProducts(withCTP);
				}
			} catch (e) {
				// ignore
			}
		}, 400);
	};


	const fetchProducts = async () => {
		setIsLocalInitialized(false);
		setLoading(true);
		setError(null);
		await fetchOptions();
		if (productsInCart.length === 0) {
			setCartProducts([]);
			setLoading(false);
			setIsLocalInitialized(true); // Mark as initialized even if empty
			return;
		}

		try {
			const productIds = uniqueProductIds;
			const reqBody = {
				products: productIds,
				site_number: selectedSite,
			};
			console.log("before calling the getprodsbyid, printing the productids",productIds);
			const response = await getProductsById(productIds,selectedSite);
			console.log("inside cart getProductsbyid response", response);
			const productData = (response?.data || []).filter(p => p !== null && p !== undefined);
			const updatedCartItems = await Promise.all(
				productsInCart.map(async (item, index) => {
					// Ensure item is not null/undefined before processing
					if (!item) return null; // Return null early for invalid items

					const matchedProduct = productData.find((product) => parseInt(product.product_id) === parseInt(getNormalizedProductId(item)));
					if (matchedProduct) {
						const imageSrc = await checkImageExists(matchedProduct.product_number);
						const showDisclaimer = ["30AA", "30BA", "3AAA", "3ABA"].includes(matchedProduct.product_class);
						if (showDisclaimer && !showDisclaimerCheckout) {
							setShowDisclaimerCheckout(true);
						}
						return {
							...item,
							product_data: matchedProduct,
							showDisclaimer,
							imageSrc,
						};
					}
					return null; // Return null if no matched product is found
				})
			);
			const finalCartItems = updatedCartItems.filter(item => item !== null); // Filter out any null entries
			console.log("Cart Products: ", finalCartItems);
			if (finalCartItems.length > 0) {
				// Optimistic render immediately
				setCartProducts(finalCartItems);
				// Debounced CTP refresh
				scheduleCTPRefresh(finalCartItems);
				setLoading(false);
				setIsLocalInitialized(true);
			} else {
				setCartProducts([]);
				setLoading(false);
				setIsLocalInitialized(true);
			}

		} catch (err) {
			setError(t('ERROR_MESSAGES.FAILED_TO_FETCH_DATA', { error: err.message }));
			setCartProducts([]);
			setLoading(false);
			setIsLocalInitialized(true);
		}
	}

	const fetchOptions = async () => {
		try {
			const response = await getOptions();
			console.log("inside cart getpotions response", response);
			const responseData = response.data.options;
			setAppOptions(responseData);
		} catch (error) {
			console.log(error);
		}
	};

	useEffect(() => {
		if (!isInitialized) return;
		fetchOptions();
	}, [isInitialized]);

    // Legacy fetchRecommendations kept for reference; now debounced inline in fetchProducts


    useEffect(() => {
        if (!isInitialized) return;

		if (!cartProductIds.length) {
			setCartProducts([]);
			setLoading(false);
			setIsLocalInitialized(true);
			return;
		}

		// Only fetch if cartProducts don’t already cover these IDs
		const localIds = cartProducts.map(p => p.product_data?.product_id).sort().join(",");
		if (localIds === cartProductIds) {
			return; // no need to fetch again
		}
        fetchProducts();
    }, [cartProductIds, isInitialized]);

	useEffect(() => {
		if (!isInitialized) return;
		if (!cartProductIds.length || !selectedSite) {
			setRecommendedProducts([]);
			return;
		}

		if (recDebounceRef.current) clearTimeout(recDebounceRef.current);
		const seq = ++recReqSeq.current;
		recDebounceRef.current = setTimeout(async () => {
			try {
				const response = await getCartRecommendations(uniqueProductIds, selectedSite);
				if (seq !== recReqSeq.current) return;
				const recommendations = Array.isArray(response?.data) ? response.data : [];
				const uniqueRecommendations = [
					...new Map(recommendations.map(item => [item.pnum, item])).values()
				];
				setRecommendedProducts(uniqueRecommendations);
			} catch (error) {
				if (seq !== recReqSeq.current) return;
				console.warn("Failed to fetch recommendations:", error);
				setRecommendedProducts([]);
			}
		}, 400);
	}, [cartProductIds, isInitialized, selectedSite, uniqueProductIds]);

    // Cleanup timers on unmount
    useEffect(() => {
        return () => {
            if (ctpDebounceRef.current) clearTimeout(ctpDebounceRef.current);
            if (recDebounceRef.current) clearTimeout(recDebounceRef.current);
        };
    }, []);


	useEffect(() => {
		if (cartProducts.length > 0) {

			const getgroupedCartProducts = cartProducts.reduce((groups, item) => {
				if (!item || !item.product_data) return groups; // Extra defensive check
				const { ctp } = item;

				// Determine the correct shipping date
				let shippingDate = null;
				if (ctp?.in_stock && ctp.in_stock.qty > 0) {
					shippingDate = ctp.in_stock.shipping_date;
				} else if (ctp?.out_of_stock && ctp?.out_of_stock?.qty > 0) {
					shippingDate = ctp.out_of_stock.shipping_date;
				} else {
					// While CTP is being fetched, use a sentinel to render skeleton
					shippingDate = '__loading__';
				}

				if (!shippingDate) {
					shippingDate = '__loading__'; // fallback group if nothing found
				}

				// Initialize group if not present
				if (!groups[shippingDate]) {
					groups[shippingDate] = [];
				}

				// Push the item into the right group
				groups[shippingDate].push(item);

				return groups;
			}, {});

			setGroupedCartProducts(getgroupedCartProducts);
		}
	}, [cartProducts]);

	useEffect(() => {
		console.log("Gropued: ", groupedCartProducts);
	}, [groupedCartProducts]);

	const parsePrice = (price) => {
		const numPrice = typeof price === "string" ? parseFloat(price) : price;
		return isNaN(numPrice) ? 0 : numPrice;
	};

	const calculateSubtotal = () => {
		console.log("In cart calculate subtotal printing cartprods", cartProducts);
		return (cartProducts || []).reduce((total, item) => {
			if (!item || !item.product_data) return total; // Skip null/undefined items
			const key = `price_${selectedSiteCurrency}`;
			const currencyPrice = item.product_data?.[key];
			const finalPrice = item.product_data?.purchasable ? currencyPrice : 0;
			const price = parsePrice(finalPrice);

			const cartItem = cartItems.find(
				(ci) =>
					getNormalizedProductId(ci) === getNormalizedProductId(item) && ci.Site === selectedSite
			);

			const quantity = cartItem ? cartItem?.Quantity : item?.Quantity;
			return total + price * quantity;
		}, 0);
	};

	const calculateTax = () => calculateSubtotal() * 0;
	const calculateTotal = () => calculateSubtotal() + calculateTax();

	const handleInputChange = (productId, inputValue) => {
		// Allow completely empty input during editing
		setQuantityInputs((prev) => ({
			...prev,
			[productId]: inputValue, // Keep the raw input as-is during typing
		}));
	};

	// Simplified blur handler that only validates on final commit
	const handleInputBlur = (productId) => {
		const inputValue = quantityInputs[productId];
		const product = cartProducts.find(
			(p) => getNormalizedProductId(p) === productId
		);

		if (!product) return;

		const maxStock = maxQtyAllowed;

		// Clean the input - remove non-digits
		const digitsOnly = inputValue ? inputValue.replace(/\D/g, "") : "";

		// If completely empty or no valid digits, set to 1
		if (!digitsOnly || digitsOnly === "") {
			setQuantityInputs((prev) => ({
				...prev,
				[productId]: "1",
			}));
			updateQuantity(productId, 1, "input");

			triggerToast(t('ALERT_MESSAGES.QUANTITY_CANNOT_BE_EMPTY'), "info");
			return;
		}

		let quantity = parseInt(digitsOnly);

		// Handle invalid numbers
		if (isNaN(quantity)) {
			setQuantityInputs((prev) => ({
				...prev,
				[productId]: "1",
			}));
			updateQuantity(productId, 1, "input");
			return;
		}

		// Validate bounds
		let finalQuantity = quantity;
		let showAlert = false;
		let alertMessage = "";

		if (quantity < 1) {
			finalQuantity = 1;
			showAlert = true;
			alertMessage = t('ALERT_MESSAGES.QUANTITY_CANNOT_BE_LESS_THAN_ONE');
		} else if (quantity > maxStock) {
			finalQuantity = maxStock;
			showAlert = true;
			alertMessage = t('ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MAX', { maxStock });
		}

		// Update both input state and cart
		setQuantityInputs((prev) => ({
			...prev,
			[productId]: finalQuantity.toString(),
		}));

		updateQuantity(productId, finalQuantity, "input");

		if (showAlert) {
			triggerToast(alertMessage, "warning");
		}
	};

	// Simplified key press handler
	const handleKeyPress = (productId, event) => {
		if (event.key === "Enter") {
			event.target.blur();
		} else if (event.key === "Escape") {
			const currentItem = cartItems.find(
				(item) => getNormalizedProductId(item) === productId && item.Site === selectedSite
			);
			const currentQuantity = currentItem ? currentItem.Quantity : 1;

			setQuantityInputs((prev) => ({
				...prev,
				[productId]: currentQuantity.toString(),
			}));
			event.target.blur();
		}
	};

	// Simplified key down handler
	const handleKeyDown = (productId, event) => {
		// Allow all normal editing keys
		const allowedKeys = [
			"Backspace",
			t("UI_TEXT.TOOLTIP_DELETE"),
			"ArrowLeft",
			"ArrowRight",
			"Home",
			"End",
			"Tab",
			"Enter",
			"Escape",
		];

		const isDigit = /^[0-9]$/.test(event.key);
		const isCtrlKey = event.ctrlKey || event.metaKey;

		// Only prevent non-digit, non-control keys
		if (!isDigit && !allowedKeys.includes(event.key) && !isCtrlKey) {
			event.preventDefault();
		}
	};

	const handleRemoveItem = (productId) => {
		removeFromCart(productId);
		setCartProducts((prev) =>
			prev.filter((item) => getNormalizedProductId(item) !== productId)
		);
		// Clean up quantity input state
		setQuantityInputs((prev) => {
			const newState = { ...prev };
			delete newState[productId];
			return newState;
		});
	};

	const handleQuantityChange = (product_id, count) => {
		const currentItem = cartItems.find(
			(item) => getNormalizedProductId(item) === product_id && item.Site === selectedSite
		);

		if (!currentItem) return;

		const newQuantity = parseInt(currentItem.Quantity || 0, 10) + count;
		const source = count > 0 ? "increment" : "decrement";
		updateQuantity(product_id, newQuantity, source);
	}

	const updateQuantity = async (productId, newQuantity, source = "direct") => {
		const cartItem = cartItems.find(
			(item) => getNormalizedProductId(item) === productId && item?.Site === selectedSite
		);

		const product = cartProducts.find(
			(p) => getNormalizedProductId(p) === productId
		);

		if (!cartItem || !product) return;

		const maxStock = maxQtyAllowed;
		let finalQuantity = newQuantity;

		if (finalQuantity < 1) {
			finalQuantity = 1;
		} else if (finalQuantity > maxStock) {
			finalQuantity = maxStock;
			if (source === "increment") {
				triggerToast(t('ALERT_MESSAGES.MAX_STOCK_LIMIT_REACHED', { maxStock }), "info");
			} else if (source === "input") {
				triggerToast(t('ALERT_MESSAGES.MAX_UNITS_ALLOWED_ADJUSTED', { maxStock }), "warning");
			}
		}

		// Update cart items
		const updatedCartItems = cartItems.map((item) => {
			if (getNormalizedProductId(item) === productId && item?.Site === selectedSite) {
				return { ...item, Quantity: finalQuantity };
			}
			return item;
		});

		const updatedCartProducts = cartProducts.map((item) => {
			if (getNormalizedProductId(item) === productId && item?.Site === selectedSite) {
				return { ...item, Quantity: finalQuantity };
			}
			return item;
		});

		console.log("Updated Qty: ", updatedCartItems, updatedCartProducts);
		// Optimistic update immediately
		setCartProducts(updatedCartProducts);
		// Debounce CTP recalculation for entire cart
		scheduleCTPRefresh(updatedCartProducts);

		updateCart(updatedCartItems);
		setQuantityInputs((prev) => ({
			...prev,
			[productId]: finalQuantity.toString(),
		}));
	}


	const showLoadingState = loading || (productsInCart.length > 0 && cartProducts.length === 0);

	if (showLoadingState) {
		// Render skeletons matching cart layout
		return (
			<Container maxWidth="xl" className="cart-container">
				<PageTitle title={t("TITLES.CART")} />
				<Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: "24px", mt: 3 }}>
					<Box sx={{ flex: 1 }}>
						<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: '10px' }}>
							<CardContent>
								<Skeleton variant="text" width={220} height={28} />
								<Divider sx={{ my: 2 }} />
								{[...Array(4)].map((_, idx) => (
									<Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
										<Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: 1 }} />
										<Box sx={{ flex: 1 }}>
											<Skeleton variant="text" width="60%" height={22} />
											<Skeleton variant="text" width="40%" height={18} />
										</Box>
										<Skeleton variant="rectangular" width={88} height={36} sx={{ borderRadius: 1 }} />
										<Skeleton variant="text" width={80} height={22} />
										<Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: '50%' }} />
									</Box>
								))}
							</CardContent>
						</Card>
					</Box>
					<Box sx={{ flex: 0.4 }}>
						<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: '10px' }}>
							<CardContent>
								<Skeleton variant="text" width={160} height={24} />
								<Divider sx={{ my: 2 }} />
								<Box className="summarySubtotal">
									<Box className="summary-row">
										<Skeleton variant="text" width={120} height={20} />
										<Skeleton variant="text" width={80} height={20} />
									</Box>
								</Box>
								<Box className="summary-row total">
									<Skeleton variant="text" width={100} height={24} />
									<Skeleton variant="text" width={100} height={24} />
								</Box>
								<Box className="summary-row total">
									<Skeleton variant="text" width={'100%'} height={14} />
								</Box>
								<Box className="summary-actions" sx={{ mt: 2 }}>
									<Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
								</Box>
							</CardContent>
						</Card>
					</Box>
				</Box>
			</Container>
		);
	}

	if (!isLocalInitialized) {
		return (
			<Box className="cart-loader">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}
	return (
		<>
			<Container maxWidth="xl" className="cart-container" sx={{ pb: 4 }} data-tour="cart-part-details">
				<PageTitle title={t("TITLES.CART")} />

				{modelViewerSummary && (
					<Alert severity="info" sx={{ mt: 2 }}>
						<Typography variant="body2">
							{modelViewerSummary.cartCount} part(s) were added to your cart and {modelViewerSummary.requestCount} part(s)
							were sent to the Request List.
						</Typography>
						<Link
							component={RouterLink}
							to="/requests"
							sx={{ mt: 1, display: "inline-flex", fontWeight: 600 }}
						>
							{t('UI_TEXT.VIEW_REQUEST_LIST')}
						</Link>
					</Alert>
				)}


				{cartProducts.length === 0 && !showLoadingState && productsInCart.length === 0 ? (
					<Box sx={{ width: '100%' }}>

						<Alert severity="info" className="empty-cart-alert">
							{t("ALERT_MESSAGES.CART_MESSAGE")}
						</Alert>
		<CartUpload onSummary={setPendingMixedSummary} />
					</Box>
				) : (<>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							justifyContent: "space-between",
							gap: "24px",
								mt: 3,
								position: "relative"
						}}
					>
							<Box sx={{ flex: 1 }}>

								<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: '10px' }} >
									<CardContent>
										<CartTable
											groupedCartProducts={groupedCartProducts}
											cartItems={cartItems}
											selectedSite={selectedSite}
											selectedSiteCurrency={selectedSiteCurrency}
											maxQtyAllowed={maxQtyAllowed}
											quantityInputs={quantityInputs}
											handleInputChange={handleInputChange}
											handleInputBlur={handleInputBlur}
											handleKeyPress={handleKeyPress}
											handleKeyDown={handleKeyDown}
											handleQuantityChange={handleQuantityChange}
											handleRemoveItem={handleRemoveItem}
											parsePrice={parsePrice}
											handleNavigateToProduct={handleNavigateToProduct}
											t={t}
										/>
									</CardContent>
								</Card>
								<Button
									variant="outlined"
									color="primary"
									sx={{ mt: 2 }}
									startIcon={<RemoveShoppingCartOutlined />}
									onClick={() => {
										clearCart();
										setCartProducts([]);
										setQuantityInputs({});
									}}
								>
									{t("BUTTON.CLEAR_CART")}
								</Button>

		<CartUpload onSummary={setPendingMixedSummary} />

								{recommendedProducts.length > 0 && (
									<Box sx={{ mt: 2 }}>
										<div
											style={{
												display: "flex",
												flexDirection: "column",
												alignItems: "flexStart",
												justifyContent: "flexStart",
											}}
										>
											<Typography variant="h6">
												{t("UI_TEXT.FREQUENTLY_TOGETHER")}
											</Typography>

											<div
												style={{
													borderBottom: "4px",
													borderColor: "var(--primary-main) !important",
													background: "var(--primary-main) !important",
													backgroundColor: "var(--primary-main) !important",
													borderBottomColor: "var(--primary-main) !important",
													height: "2px",
													margin: "2px 0",
													width: "90px",
												}}
											></div>
										</div>

										<Box>
											{recommendedProducts.slice(0, 5).filter(product => product !== null && product !== undefined).map((product) => (
											<TopProductListItem
												key={product.product_id}
												product={product}
												machine_id={product.machine_id}
											/>
										))}
										</Box>
									</Box>
								)}
							</Box>
							<Box sx={{ flex: 0.4 }}>
								<Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: '10px', position: 'sticky', top: DEFAULT_LAYOUT === "layout2" ? "150px" : "100px" }} >
									<CardContent>
										<Typography variant="h6" gutterBottom>
											{t("UI_TEXT.ORDER_SUMMARY")}
										</Typography>
										<Box className="summarySubtotal">
											<Box className="summary-row">
												<Typography>{t("TABLE_HEADERS.SUBTOTAL")}</Typography>
												<Typography>
													{formatCurrency(
														calculateSubtotal(),
														selectedSiteCurrency
													)}
												</Typography>
											</Box>
										</Box>
										<Box className="summary-row total">
											<Typography variant="h6">
												{t("TABLE_HEADERS.TOTAL")}
											</Typography>
											<Typography variant="h6">
												{formatCurrency(calculateTotal(), selectedSiteCurrency)}
											</Typography>
										</Box>
										<Box className="summary-row total">
											<Typography variant="body1" sx={{ fontSize: "11px" }}>
												{t("UI_TEXT.NOTE_SHIPPING_TAXES_INVOICE")}
											</Typography>
										</Box>
										{showDisclaimerCheckout && (
											<Box mt={1}>
												<Alert severity="warning">
													<Typography fontWeight="bold">
														{t('UI_TEXT.IMPORTANT_NOTICE_FINAL_SALE')}
													</Typography>
													<Typography sx={{ fontSize: "11px" }}>
														{t('UI_TEXT.CUSTOM_MANUFACTURED_DISCLAIMER')}</Typography>
													<Typography mt={1} sx={{ fontSize: "11px" }}>
														<strong>  {t('UI_TEXT.NO_RETURNS')}</strong> {t('UI_TEXT.FINAL_SALE')}
													</Typography>
												</Alert>
											</Box>
										)}
										<Box className="summary-actions">
											<Button
												variant="contained"
												color="primary"
												fullWidth
												className="checkout-button"
												data-tour="cart-checkout-button"
												onClick={() => {
													navigate(ROUTES.CHECKOUT);
												}}
												disabled={
													!canPlaceOrder ||
													calculateTotal().toFixed(2) <
													(parseFloat(appOptions.minOrderAmount) || parseFloat(0))
												}
											>
												{t("BUTTON.CHECKOUT")}
											</Button>
											{calculateTotal().toFixed(2) <
												(parseFloat(appOptions.minOrderAmount) ||
													parseFloat(0)) && (
													<Typography variant="body1" color="error">
														{t("UI_TEXT.MINIMUM_ORDER_AMOUNT_SHOULD_BE")}{" "}
														{currencySymbol}
														{parseFloat(appOptions.minOrderAmount) ||
															parseFloat(0)},{" "}
														{t("UI_TEXT.YOUR_CURRENT_ORDER_TOTAL_IS")}
														{currencySymbol}
														{calculateTotal().toFixed(2)}
													</Typography>
												)}
											{!canPlaceOrder && (
												<Typography variant="body1" color="error">
													{t("UI_TEXT.YOU_ARE_NOT_AUTHORIZED_ORDER")} <br />
													{t(
														"UI_TEXT.PLEASE_CONTACT_THE_CUTOMER_CARE_FOR_FURTHER_DETAILS"
													)}
												</Typography>
											)}
										</Box>
									</CardContent>
								</Card>
							</Box>
						</Box>
				</>)}
			</Container>
		</>
	)
}

export default Cart
